import React, { useState, useEffect } from 'react';
import { Eye, Trash2, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api.ts';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'unread', 'read'
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5,
  });
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";


  // Fetch notifications
  const fetchNotifications = async (page = 1, status = 'all') => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const params = {
        page: page,
        limit: 5, // Match server's itemsPerPage
        orderBy: 'createdAt',
        order: 'desc'
      };

      const response = await api.get('/notifications/getusernotifications', { params });
      const data = response.data;
      
      if (data.success) {
        const notificationsData = data.data || [];
        
        setNotifications(notificationsData);
        setPagination({
          currentPage: data.pagination?.currentPage || 1,
          totalPages: data.pagination?.totalPages || 1,
          totalItems: data.pagination?.totalItems || 0,
          itemsPerPage: data.pagination?.itemsPerPage || 5,
        });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/marknotificationasread/${notificationId}`);
      
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, isRead: true }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark notification as unread
  const markAsUnread = async (notificationId) => {
    try {
      await api.put(`/notifications/marknotificationasunread/${notificationId}`);
      
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, isRead: false }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      // Mark each unread notification as read
      const unreadNotifications = notifications.filter(n => !n.isRead);
      await Promise.all(
        unreadNotifications.map(notif => 
          api.put(`/notifications/marknotificationasread/${notif.id}`)
        )
      );
      
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, isRead: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchNotifications(1, tab);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchNotifications(page, activeTab);
  };

  // Fetch notifications when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      fetchNotifications(1, activeTab);
    }
  }, [user?.id]);

  if (!user?.id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {translate('notifications.loginRequired') || 'Login Required'}
          </h2>
          <p className="text-gray-600">
            {translate('notifications.pleaseLogin') || 'Please login to view notifications'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-md shadow overflow-x-auto mx-1 md:mx-0" dir={isRTL ? "rtl" : "ltr"} lang={language}>
      {/* Header */}
      <div className="bg-primary-soft text-gray-700 font-medium rounded-t-[16px] ">
        <div className="p-3 sm:p-[20px] text-lg rounded-t-[12px] font-semibold text-white bg-primary-soft">
          <div className="flex items-center justify-between">
            <h2 className="text-sm sm:text-base lg:text-lg font-semibold">
              {translate('notifications.title') || 'Notifications'}
            </h2>
            {/* Tabs */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleTabChange('all')}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  activeTab === 'all'
                    ? 'bg-primary-dark text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                }`}
              >
                {translate('notifications.all') || 'All'}
              </button>
              <button
                onClick={() => handleTabChange('unread')}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  activeTab === 'unread'
                    ? 'bg-primary-dark text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                }`}
              >
                {translate('notifications.unread') || 'Unread'}
              </button>
              <button
                onClick={() => handleTabChange('read')}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  activeTab === 'read'
                    ? 'bg-primary-dark text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                }`}
              >
                {translate('notifications.read') || 'Read'}
              </button>
            </div>
          </div>
        </div>
      </div>


        {/* Actions */}
        <div className="mb-6 flex justify-between items-center">
          
          {/* {activeTab === 'unread' && notifications.length > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors duration-200"
            >
              {translate('notifications.markAllRead') || 'Mark All Read'}
            </button>
          )} */}
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-gray-500">{translate('notifications.loading') || 'Loading...'}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">
                {activeTab === 'all' 
                  ? (translate('notifications.noNotifications') || 'No notifications')
                  : (translate('notifications.noUnreadNotifications') || 'No unread notifications')
                }
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-page rounded-lg shadow-sm border border-default p-6 ${
                  !notification.isRead ? 'border-l-4 border-l-primary' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Status and Title */}
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        !notification.isRead
                          ? 'bg-primary/10 text-primary'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {!notification.isRead ? (
                          <>
                            <Clock className="w-3 h-3 mr-1" />
                            {translate('notifications.unread') || 'Unread'}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {translate('notifications.read') || 'Read'}
                          </>
                        )}
                      </span>
                      <h3 className={`text-lg font-semibold ${
                        !notification.isRead ? 'text-white' : 'text-white'
                      }`}>
                        {notification.notificationEvent?.name || 'Notification'}
                      </h3>
                    </div>

                    {/* Message */}
                    <p className="text-description mb-3">
                      {notification.description}
                    </p>
                  </div>

                  {/* Date and Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    {/* Date */}
                    <p className="text-sm text-gray-500">
                      {formatDateTime(notification.createdAt)}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex space-x-2">
                    {!notification.isRead ? (
                      <button
                        onClick={() => markAsRead(notification.id)}
                           className="inline-flex items-center px-3 py-2  shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                       
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {translate('notifications.markRead') || 'Mark Read'}
                      </button>
                    ) : (
                      <button
                        onClick={() => markAsUnread(notification.id)}
                     


                         className="inline-flex items-center px-3 py-2 shadow-sm text-sm leading-4 font-medium rounded-md text-black bg-secondary hover:bg-secondary-dark"
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        {translate('notifications.markUnread') || 'Mark Unread'}
                      </button>
                    )}
                    {/* <button
                      onClick={() => deleteNotification(notification.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {translate('notifications.delete') || 'Delete'}
                    </button> */}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-8 p-4 flex items-center justify-between">
            <div className="text-sm text-description">
              {translate('notifications.showing') || 'Showing'} {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} {translate('notifications.to') || 'to'} {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} {translate('notifications.of') || 'of'} {pagination.totalItems} {translate('notifications.results') || 'results'}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate('notifications.previous') || 'Previous'}
              </button>
              <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md">
                {pagination.currentPage} {translate('notifications.of') || 'of'} {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translate('notifications.next') || 'Next'}
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default NotificationsPage;
